import { PropsWithChildren, useEffect } from "react";
import type { AxiosError } from "axios";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { useUsers } from "~/hooks";
import { isDeepLinkRoute, ROUTES } from "~/router";
import { Role } from "~/shared.types";
import { useAuthStore, useUserStore } from "~/stores";

interface ProtectedRouteProps extends PropsWithChildren {
  only?: Role | Role[];
  redirectTo?: string;
}

export const ProtectedRoute = ({
  children,
  only,
  redirectTo,
}: ProtectedRouteProps) => {
  const { getCurrentUser } = useUsers();
  const accessToken = useAuthStore((state) => state.accessToken);
  const location = useLocation();

  const { data: currentUser, isLoading, error } = getCurrentUser(!!accessToken);

  const navigate = useNavigate();
  const clearStore = useUserStore((state) => state.clearStore);
  const clearAuthStore = useAuthStore((state) => state.clearStore);
  const setUserRole = useUserStore((state) => state.setUserRole);
  const setState = useUserStore((state) => state.setState);

  useEffect(() => {
    if (!accessToken) {
      window.location.href = isDeepLinkRoute(location.pathname)
        ? `${ROUTES.fullLogin}?redirect=${encodeURIComponent(location.pathname)}`
        : ROUTES.fullLogin;
      return;
    }

    if (error) {
      const status = (error as AxiosError)?.response?.status;
      if (status === 401 || status === 419) {
        clearStore();
        clearAuthStore();
        window.location.href = isDeepLinkRoute(location.pathname)
          ? `${ROUTES.fullLogin}?redirect=${encodeURIComponent(location.pathname)}`
          : ROUTES.fullLogin;
        return;
      }
      if (!currentUser) {
        clearStore();
        clearAuthStore();
        navigate(ROUTES.fullLogin, { replace: true });
        return;
      }
    }

    if (currentUser) {
      if (only) {
        const roles = Array.isArray(only) ? only : [only];

        if (!roles.includes(currentUser.userType)) {
          if (redirectTo) {
            navigate(redirectTo, { replace: true });
          } else {
            clearStore();
            clearAuthStore();
            navigate(ROUTES.fullLogin, { replace: true });
          }
          return;
        }
      }

      setUserRole(currentUser.userType);

      if (currentUser.stateId) {
        setState(currentUser.stateId);
      }
    }
  }, [
    accessToken,
    currentUser,
    error,
    only,
    redirectTo,
    setUserRole,
    setState,
    navigate,
    clearStore,
    clearAuthStore,
    location.pathname,
  ]);

  if (isLoading || !currentUser) {
    return <></>;
  }

  if (only) {
    const roles = Array.isArray(only) ? only : [only];
    if (!roles.includes(currentUser.userType)) {
      return <></>;
    }
  }

  return children ? <>{children}</> : <Outlet />;
};
